import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Search,
    Sparkles,
    ArrowRight,
    Bot,
    ChevronRight,
    Layers,
    MessageSquare,
    Zap,
    ShieldCheck,
    Info,
    History,
    X,
    Send,
    Loader2,
    CheckCircle2,
    Scale,
    Gavel,
    ScrollText,
    Download,
    Paperclip,
    FileUp,
    ChevronUp,
    ChevronDown,
    Maximize2,
    Minimize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LegalEditor from "@/components/editor/LegalEditor";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { DynamicIntakeForm } from "@/components/chat/DynamicIntakeForm";
import { useApiClient } from "@/hooks/use-api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { SPECIALIZED_AGENTS_META, AGENT_CATEGORIES, AgentMeta } from "@/lib/specialized-agents-meta";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { marked } from "marked";

// --- Types ---

type ViewMode = "ROUTER" | "DIRECTORY" | "AGENT";

// --- Components ---

const PremiumBackground = () => (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px]" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-indigo-500/5 rounded-full blur-[120px]" />
        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] bg-purple-500/5 rounded-full blur-[120px]" />
    </div>
);

export default function SpecializedAgentsV2() {
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startSpecializedAgentRun, getRun, resetRun, updateRun } = useAgentExecution();

    const [viewMode, setViewMode] = useState<ViewMode>("ROUTER");
    const [searchQuery, setSearchQuery] = useState("");
    const [userQuery, setUserQuery] = useState("");
    const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
    const [isRouting, setIsRouting] = useState(false);
    const [routingLog, setRoutingLog] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [leftCollapsed, setLeftCollapsed] = useState(false);
    const [rightCollapsed, setRightCollapsed] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const agentFileInputRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef(null);

    // Editing State
    const isMobile = useIsMobile();
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState("");
    const [workspaceTab, setWorkspaceTab] = useState<"CHAT" | "EDITOR" | "TRACE" | "INSIGHTS">("CHAT");
    const [headerCollapsed, setHeaderCollapsed] = useState(false);

    const activeAgent = selectedAgentId ? SPECIALIZED_AGENTS_META[selectedAgentId] : null;
    const runId = `specialized-${selectedAgentId || 'router'}`;
    const currentRun = getRun(runId);

    // Filter agents by category
    const agentsByCategory = useMemo(() => {
        const acc: Record<string, AgentMeta[]> = {};
        Object.values(SPECIALIZED_AGENTS_META).forEach(agent => {
            if (!acc[agent.category]) acc[agent.category] = [];
            acc[agent.category].push(agent);
        });
        return acc;
    }, []);

    // --- Handlers ---

    const handleMasterRoute = async () => {
        if (!searchQuery.trim() && !selectedFile) {
            toast.error("Por favor ingresa una consulta o sube un documento.");
            return;
        }

        setIsRouting(true);
        setRoutingLog(["Iniciando Agente Maestro...", "Preparando datos...", selectedFile ? `Procesando archivo: ${selectedFile.name}` : "Analizando texto..."]);

        try {
            let response;

            if (selectedFile) {
                // Pass as plain object, HttpClient will handle FormData conversion
                response = await (apiClient as any).post("/api/v1/agents/route-file", {
                    file: selectedFile
                });
            } else {
                response = await (apiClient as any).post("/api/v1/agents/route", {
                    message: searchQuery
                });
            }

            const { selected_agent_id, reason } = response.data;
            const agentId = selected_agent_id; // Mapping from selected_agent_id to agent_id

            setTimeout(() => setRoutingLog(prev => [...prev, "Extrayendo inteligencia legal...", `Detectado: Especialista en ${SPECIALIZED_AGENTS_META[agentId]?.name || agentId}`]), 1000);
            setTimeout(() => setRoutingLog(prev => [...prev, `Motivo: ${reason}`]), 2000);

            // Artificial delay for wow factor
            setTimeout(() => {
                setSelectedAgentId(agentId);
                setViewMode("AGENT");
                setIsRouting(false);
                setRoutingLog([]);
                setSelectedFile(null); // Clear file after successful routing

                // Automatically start the question in the specialized agent
                // If we have a file, we might want to pass the extracted text as a message
                const initialMessage = response.data.extracted_text_preview || searchQuery;
                handleAskAgent(agentId, initialMessage);
            }, 3500);

        } catch (error: any) {
            console.error("Routing error:", error);
            setIsRouting(false);
            setRoutingLog([]);
            toast.error("No se pudo determinar el especialista automáticamente. Por favor selecciónalo manualmente.");
            setViewMode("DIRECTORY");
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            toast.success(`Archivo seleccionado: ${file.name}`);
        }
    };

    const handleAskAgent = async (agentId: string, message: string) => {
        const agentRunId = `specialized-${agentId}`;
        const existingRun = getRun(agentRunId);
        const history = existingRun?.messages || [];

        await startSpecializedAgentRun(agentId, agentRunId, { message, history }, apiClient, user?.id || null);
        setUserQuery("");
    };

    const handleSelectAgent = (agentId: string) => {
        setSelectedAgentId(agentId);
        setViewMode("AGENT");
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleIntakeSubmit = async (field: string, value: string) => {
        if (!activeAgent) return;

        // Clear pending input visually (the backend run update will handle the state)
        updateRun(runId, { pendingInput: null, status: 'loading' });

        // Send as a structured message
        const message = `DATO_INGESTADO [${field}]: ${value}`;
        await handleAskAgent(activeAgent.id, message);
    };

    // --- Effects ---

    // Deep sync: when the AI agent proposes a document update via chat metadata
    useEffect(() => {
        if (currentRun?.metadata?.document_update && currentRun?.metadata?.last_sync) {
            const newContent = currentRun.metadata.document_update;
            // Only update if content is actually different and it's a new sync event
            if (newContent !== editedContent) {
                setEditedContent(newContent);
                toast.success("Documento actualizado por la IA", {
                    description: "Se han aplicado los cambios solicitados en la pestaña 'Documento'.",
                    duration: 5000,
                });
            }
        }
    }, [currentRun?.metadata?.document_update, currentRun?.metadata?.last_sync]);

    useEffect(() => {
        if (viewMode === "AGENT" && selectedAgentId) {
            // Log analytics or init
        }
    }, [viewMode, selectedAgentId]);

    const handleBack = () => {
        if (viewMode === "AGENT") {
            setViewMode("DIRECTORY");
        } else {
            setViewMode("ROUTER");
        }
        setIsEditing(false);
        setEditedContent("");
        resetRun(runId);
    };

    const handleSaveEdit = async (contentOverride?: string) => {
        const finalContent = contentOverride || editedContent;
        if (!currentRun?.metadata?.answer || !finalContent) return;

        try {
            await (apiClient as any).post("/api/v1/agents/save-edit", {
                session_id: runId,
                agent_id: selectedAgentId,
                original_content: currentRun.metadata.answer,
                edited_content: finalContent,
                user_id: user?.id
            });

            toast.success("Respuesta editada guardada en el historial");
            setIsEditing(false);
            setWorkspaceTab("CHAT");
        } catch (error) {
            toast.error("Error al guardar la edición");
        }
    };

    const startEditing = () => {
        setEditedContent(currentRun?.metadata?.answer || "");
        setIsEditing(true);
        setWorkspaceTab("EDITOR");
    };

    // --- Renderers ---

    const renderRouter = () => (
        <div
            className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4 animate-in fade-in slide-in-from-bottom-5 duration-700"
        >
            <div className="mb-8 p-4 bg-blue-600/10 rounded-3xl">
                <Sparkles className="w-12 h-12 text-blue-600 animate-pulse" />
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight">
                El futuro de la <span className="text-blue-600">Asesoría Legal</span>
            </h1>
            <p className="text-sm sm:text-lg text-slate-600 max-w-2xl mb-8 sm:mb-12">
                Escribe tu consulta o adjunta un expediente. Nuestro Agente Maestro identificará automáticamente al especialista más adecuado entre nuestros 23 expertos colombianos.
            </p>

            <div className="w-full max-w-3xl relative">
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2rem] blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
                    <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center bg-white border border-slate-200 rounded-2xl sm:rounded-[1.8rem] p-2 shadow-2xl overflow-hidden gap-2 sm:gap-0">
                        <div className="pl-3 sm:pl-6 hidden sm:flex items-center text-slate-400">
                            <Search className="w-6 h-6" />
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept=".pdf,.docx,.doc"
                            onChange={handleFileSelect}
                        />
                        <div className="flex-1 flex items-center pr-2 bg-slate-50/50 rounded-xl sm:rounded-2xl mx-1 sm:mx-2">
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleMasterRoute()}
                                placeholder={selectedFile ? `📎 Archivo: ${selectedFile.name}` : "Describe tu caso o sube documento..."}
                                className={`border-none focus-visible:ring-0 text-base sm:text-lg h-12 sm:h-16 w-full placeholder:text-slate-500 font-medium ${selectedFile ? 'text-blue-700 font-black' : ''}`}
                                readOnly={!!selectedFile}
                            />
                            {selectedFile ? (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSelectedFile(null)}
                                    className="text-red-500 hover:bg-red-50"
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="rounded-full h-10 w-10 sm:h-12 sm:w-12 hover:bg-blue-50 text-blue-600 border border-blue-100/50 ml-1"
                                    title="Adjuntar documento (PDF/DOCX)"
                                >
                                    <Paperclip className="w-5 h-5 sm:w-6 sm:h-6" />
                                </Button>
                            )}
                        </div>
                        <Button
                            onClick={handleMasterRoute}
                            disabled={isRouting}
                            className={`h-12 sm:h-14 px-6 sm:px-8 rounded-xl sm:rounded-[1.4rem] ${selectedFile ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-black text-base sm:text-lg shadow-lg shadow-blue-200 transition-all hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto`}
                        >
                            {isRouting ? <Loader2 className="w-6 h-6 animate-spin" /> : (selectedFile ? "Analizar Archivo" : "Consultar Maestro")}
                        </Button>
                    </div>
                </div>

                {isRouting && (
                    <div className="mt-8 text-left space-y-3 bg-slate-50/50 rounded-2xl p-6 border border-slate-200/50 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300">
                        {routingLog.map((log, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-3 text-sm font-medium text-slate-600 animate-in slide-in-from-left-2 duration-300"
                            >
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                {log}
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-12 flex flex-wrap justify-center gap-4">
                    <Button
                        variant="ghost"
                        onClick={() => setViewMode("DIRECTORY")}
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50/50 gap-2 font-semibold"
                    >
                        <Layers className="w-4 h-4" />
                        Explorar Directorio Manual
                    </Button>
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-slate-100 rounded-full text-xs font-bold text-slate-500 uppercase tracking-widest">
                        <Zap className="w-3 h-3 text-amber-500 fill-amber-500" />
                        23 Agentes Disponibles
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDirectory = () => (
        <div className="max-w-7xl mx-auto py-12 px-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 mb-2">Directorio de Expertos</h2>
                    <p className="text-slate-500 font-medium">Selecciona manualmente el especialista para tu caso.</p>
                </div>
                <Button variant="outline" onClick={() => setViewMode("ROUTER")} className="rounded-full gap-2 border-slate-200">
                    <Search className="w-4 h-4" /> Volver al Maestro
                </Button>
            </div>

            <Tabs defaultValue="Solicitud" className="w-full">
                <TabsList className="bg-transparent h-auto flex flex-wrap gap-4 mb-12 border-none overflow-x-auto">
                    {AGENT_CATEGORIES.map(category => (
                        <TabsTrigger
                            key={category.id}
                            value={category.id}
                            className="rounded-2xl px-6 py-4 border border-slate-200 data-[state=active]:bg-white data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-lg shadow-blue-100/50 transition-all flex flex-col items-center gap-2 min-w-[160px]"
                        >
                            <category.icon className="w-6 h-6" />
                            <div className="font-bold whitespace-nowrap">{category.label}</div>
                        </TabsTrigger>
                    ))}
                </TabsList>

                {AGENT_CATEGORIES.map(category => (
                    <TabsContent key={category.id} value={category.id} className="mt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {agentsByCategory[category.id]?.map(agent => (
                                <div
                                    key={agent.id}
                                    className={`group relative p-6 bg-white border border-slate-200 rounded-3xl cursor-pointer hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-500/10 transition-all hover:-translate-y-1 ${agent.bgHover}`}
                                    onClick={() => handleSelectAgent(agent.id)}
                                >
                                    <div className={`p-4 rounded-2xl bg-white shadow-sm border border-slate-100 w-fit mb-6 ${agent.color}`}>
                                        <agent.icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                                        {agent.name}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed mb-6">
                                        {agent.description}
                                    </p>
                                    <div className="flex items-center text-blue-600 font-bold text-sm">
                                        Iniciar Consulta <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );

    const renderAgentWorkspace = () => {
        if (!activeAgent) return null;

        return (
            <div className="flex flex-col h-[calc(100vh-100px)] max-w-[1600px] mx-auto px-2 sm:px-6 py-2 sm:py-4 animate-in fade-in duration-500">
                {/* Header */}
                <div className={`flex items-center justify-between bg-white border border-slate-200 rounded-2xl sm:rounded-3xl shadow-sm transition-all duration-300 ease-in-out ${headerCollapsed ? 'p-2 mb-2' : 'p-3 sm:p-4 mb-3 sm:mb-6'}`}>
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Button variant="ghost" size="icon" onClick={handleBack} className={`rounded-full text-slate-400 ${headerCollapsed ? 'h-8 w-8' : ''}`}>
                            <X className={headerCollapsed ? "w-4 h-4" : "w-5 h-5 sm:w-6 sm:h-6"} />
                        </Button>

                        {!headerCollapsed && (
                            <>
                                <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-slate-50 ${activeAgent.color}`}>
                                    <activeAgent.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base sm:text-xl font-black text-slate-900">{activeAgent.name}</h2>
                                        <Badge variant="outline" className="hidden sm:inline-flex text-[10px] uppercase tracking-tighter font-bold bg-blue-50 border-blue-100 text-blue-600">
                                            Especialista On-Line
                                        </Badge>
                                    </div>
                                    <p className="text-[10px] sm:text-xs text-slate-500 font-medium line-clamp-1">{activeAgent.description}</p>
                                </div>
                            </>
                        )}

                        {headerCollapsed && (
                            <div className="flex items-center gap-2 px-2">
                                <div className={`p-1.5 rounded-lg bg-slate-50 ${activeAgent.color}`}>
                                    <activeAgent.icon className="w-3.5 h-3.5" />
                                </div>
                                <h2 className="text-xs sm:text-sm font-black text-slate-900">{activeAgent.name}</h2>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200 shadow-inner">
                        <Button
                            variant={workspaceTab === "CHAT" ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setWorkspaceTab("CHAT")}
                            className={`rounded-xl gap-1.5 h-8 px-2.5 sm:px-4 transition-all text-xs ${workspaceTab === "CHAT" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <MessageSquare className="w-3.5 h-3.5" /> Chat
                        </Button>
                        <Button
                            variant={workspaceTab === "EDITOR" ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setWorkspaceTab("EDITOR")}
                            className={`rounded-xl gap-1.5 h-8 px-2.5 sm:px-4 transition-all text-xs ${workspaceTab === "EDITOR" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <ScrollText className="w-3.5 h-3.5" /> Doc
                        </Button>
                        {isMobile && (
                            <>
                                <Button
                                    variant={workspaceTab === "TRACE" ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setWorkspaceTab("TRACE")}
                                    className={`rounded-xl gap-1.5 h-8 px-2 sm:px-3 transition-all text-xs ${workspaceTab === "TRACE" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Zap className="w-3.5 h-3.5" /> Proceso
                                </Button>
                                <Button
                                    variant={workspaceTab === "INSIGHTS" ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setWorkspaceTab("INSIGHTS")}
                                    className={`rounded-xl gap-1.5 h-8 px-2 sm:px-3 transition-all text-xs ${workspaceTab === "INSIGHTS" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Scale className="w-3.5 h-3.5" /> Insights
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {currentRun?.metadata?.answer && workspaceTab === "CHAT" && !headerCollapsed && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={startEditing}
                                className="rounded-full gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 font-bold"
                            >
                                <Sparkles className="w-4 h-4" /> Editar Respuesta AI
                            </Button>
                        )}

                        {!headerCollapsed && (
                            <Button variant="outline" size="sm" className="rounded-full gap-2 text-slate-600 border-slate-200">
                                <History className="w-4 h-4" /> Historial
                            </Button>
                        )}

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setHeaderCollapsed(!headerCollapsed)}
                            className="rounded-full text-slate-400 hover:bg-slate-100"
                            title={headerCollapsed ? "Expandir Cabecera" : "Contraer Cabecera"}
                        >
                            {headerCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                        </Button>
                    </div>
                </div>

                {/* Body - 3 Panels Architecture */}
                <div className="flex flex-1 gap-4 sm:gap-6 overflow-hidden min-h-0 relative">

                    {/* Panel 1: Trace & Context (Left) */}
                    {(!isMobile || workspaceTab === "TRACE") && (
                    <div className={cn("flex flex-col gap-6 overflow-y-auto custom-scrollbar transition-all duration-300 ease-in-out relative group", isMobile ? "w-full" : (leftCollapsed ? 'w-0 opacity-0 -mr-6' : 'w-80 opacity-100 pr-2'))}>
                        {/* Always visible toggle handle when collapsed */}
                        {leftCollapsed && (
                            <button
                                onClick={() => setLeftCollapsed(false)}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-40 h-20 w-4 bg-blue-600 rounded-r-xl shadow-lg flex items-center justify-center text-white hover:w-6 transition-all"
                                title="Expandir Análisis"
                            >
                                <ChevronRight className="w-3 h-3" />
                            </button>
                        )}

                        {/* Toggle Button Left (when expanded) */}
                        {!leftCollapsed && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setLeftCollapsed(true)}
                                className="absolute top-2 right-4 z-20 h-8 w-8 rounded-full bg-slate-50 border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <ChevronRight className="w-4 h-4 rotate-180" />
                            </Button>
                        )}
                        <section className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                                <Zap className="w-3 h-3 text-amber-500" /> Proceso de Análisis
                            </h3>
                            <div className="space-y-6">
                                {(currentRun?.traceSteps || []).length > 0 ? (
                                    currentRun?.traceSteps.map((step, i) => (
                                        <div key={i} className="flex gap-4 group">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shadow-sm transition-all ${step.status === 'running' ? 'bg-blue-600 text-white animate-pulse ring-4 ring-blue-50' :
                                                        step.status === 'done' ? 'bg-emerald-100 text-emerald-600' :
                                                            'bg-slate-100 text-slate-400'
                                                    }`}>
                                                    {step.status === 'done' ? <CheckCircle2 className="w-4 h-4" /> : step.emoji}
                                                </div>
                                                {i < currentRun!.traceSteps.length - 1 && (
                                                    <div className="w-0.5 h-10 bg-slate-100 group-hover:bg-blue-100 transition-colors" />
                                                )}
                                            </div>
                                            <div className="pt-1">
                                                <div className={`text-sm font-bold ${step.status === 'running' ? 'text-blue-600' : 'text-slate-700'
                                                    }`}>
                                                    {step.message}
                                                </div>
                                                {step.detail && <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{step.detail}</p>}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                        <Bot className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                        <p className="text-xs text-slate-400 font-medium max-w-[150px] mx-auto">
                                            Inicia una consulta para ver el despliegue lógico de la IA.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                                <Info className="w-3 h-3 text-blue-500" /> Sugerencias
                            </h3>
                            <div className="space-y-3">
                                {(currentRun?.metadata?.suggestions || activeAgent.suggestedQueries).map((q: string, i: number) => (
                                    <button
                                        key={i}
                                        onClick={() => handleAskAgent(activeAgent.id, q)}
                                        className="w-full text-left p-4 rounded-2xl bg-slate-50 hover:bg-blue-50 border border-transparent hover:border-blue-100 text-xs font-semibold text-slate-600 hover:text-blue-600 transition-all font-inter"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>
                    )}

                    {/* Panel 2: Chat & Results (Middle) */}
                    {(!isMobile || workspaceTab === "CHAT" || workspaceTab === "EDITOR") && (
                    <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-2xl sm:rounded-[2.5rem] shadow-xl overflow-hidden relative">
                        {workspaceTab === "CHAT" ? (
                            <>
                                <ScrollArea className="flex-1 p-8">
                                    {/* Welcome Bubble */}
                                    <div className="flex gap-4 mb-8">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 ${activeAgent.color} bg-white`}>
                                            <Bot className="w-6 h-6" />
                                        </div>
                                        <div className="bg-slate-50 border border-slate-100 rounded-[2rem] rounded-tl-none p-6 max-w-[85%] shadow-sm">
                                            <p className="text-slate-700 text-sm leading-relaxed font-medium">
                                                {activeAgent.welcomeMessage}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Streamed Result */}
                                    {currentRun?.metadata?.answer && (
                                        <div className="flex gap-4 mb-8">
                                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 ${activeAgent.color} bg-white ring-2 ring-blue-100/50`}>
                                                <Bot className="w-6 h-6" />
                                            </div>
                                            <div className="bg-white border border-slate-100 rounded-[2rem] rounded-tl-none p-8 max-w-[95%] shadow-2xl shadow-blue-500/5 min-h-[100px] w-full">
                                                <div
                                                    className="prose prose-sm max-w-none text-slate-700 font-medium leading-relaxed"
                                                    dangerouslySetInnerHTML={{ __html: marked.parse(currentRun?.metadata?.answer || "", { async: false }) as string }}
                                                />
                                                {currentRun?.status === 'streaming' && (
                                                    <div className="mt-4 flex gap-1 items-center">
                                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Interactive Intake Form */}
                                    {currentRun?.pendingInput && (
                                        <div className="flex gap-4 mb-8 justify-center">
                                            <DynamicIntakeForm
                                                type={currentRun.pendingInput.type}
                                                label={currentRun.pendingInput.label}
                                                field={currentRun.pendingInput.field}
                                                options={currentRun.pendingInput.options}
                                                onSubmit={handleIntakeSubmit}
                                            />
                                        </div>
                                    )}

                                    {/* Error State */}
                                    {currentRun?.status === 'error' && (
                                        <div className="p-6 bg-red-50 border border-red-100 rounded-3xl text-red-600 flex items-center gap-3">
                                            <ShieldCheck className="w-5 h-5" />
                                            <p className="text-sm font-bold">Ocurrió un error en el procesamiento. Por favor reintenta.</p>
                                        </div>
                                    )}
                                </ScrollArea>

                                {/* Input Area */}
                                <div className="p-8 bg-slate-50/50 backdrop-blur-md border-t border-slate-100">
                                    <div className="relative group">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[1.8rem] blur opacity-10 group-focus-within:opacity-20 transition duration-1000"></div>
                                        <div className="relative flex items-center bg-white border border-slate-200 rounded-[1.6rem] p-2 pr-4 shadow-sm group-focus-within:shadow-xl transition-all">
                                            <input
                                                type="file"
                                                ref={agentFileInputRef}
                                                className="hidden"
                                                accept=".pdf,.docx,.doc"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        toast.info("Análisis de archivo iniciado...");
                                                        handleAskAgent(activeAgent.id, `Analiza el contenido de este archivo: ${file.name}`);
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => agentFileInputRef.current?.click()}
                                                className="ml-2 text-slate-400 hover:text-blue-600"
                                            >
                                                <FileUp className="w-5 h-5" />
                                            </Button>
                                            <Input
                                                className="border-none focus-visible:ring-0 text-slate-800 placeholder:text-slate-400 h-14 pl-2"
                                                placeholder={`Pregúntale a tu especialista en ${activeAgent.name}...`}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.currentTarget.value) {
                                                        handleAskAgent(activeAgent.id, e.currentTarget.value);
                                                        e.currentTarget.value = "";
                                                    }
                                                }}
                                            />
                                            <Button className="rounded-2xl w-12 h-12 p-0 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200">
                                                <Send className="w-5 h-5 text-white" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col animate-in fade-in zoom-in-95 duration-500 overflow-hidden">
                                <div className="flex-1 overflow-hidden">
                                    <LegalEditor
                                        documentId={runId}
                                        initialContent={editedContent}
                                        userName={user?.email?.split('@')[0] || "Abogado"}
                                        userId={user?.id}
                                        onSave={(content) => {
                                            setEditedContent(content);
                                            handleSaveEdit(content);
                                        }}
                                    />
                                </div>
                                <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                                    <Button variant="ghost" className="rounded-xl border-slate-200" onClick={() => setWorkspaceTab("CHAT")}>
                                        Volver al Chat
                                    </Button>
                                    <Button
                                        onClick={() => { handleSaveEdit(); }}
                                        className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-lg shadow-blue-200 px-8"
                                    >
                                        <CheckCircle2 className="w-4 h-4" /> Guardar y Finalizar
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Expand Button Right */}
                    {!isMobile && rightCollapsed && (
                        <button
                            onClick={() => setRightCollapsed(false)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-30 h-16 w-6 bg-white border border-slate-200 rounded-l-xl shadow-md flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all border-r-0"
                            title="Mostrar Insights"
                        >
                            <ChevronRight className="w-4 h-4 rotate-180" />
                        </button>
                    )}

                    {/* Panel 3: Insights (Right) */}
                    {(!isMobile ? (!rightCollapsed && true) : workspaceTab === "INSIGHTS") && (
                    <div className={cn("flex flex-col gap-6 overflow-y-auto custom-scrollbar transition-all duration-300 ease-in-out relative", isMobile ? "w-full" : (rightCollapsed ? 'w-0 opacity-0 -mr-6' : 'w-96 opacity-100 pl-2'))}>
                        {/* Toggle Button Right */}
                        {!rightCollapsed && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setRightCollapsed(true)}
                                className="absolute top-2 left-4 z-20 h-8 w-8 rounded-full bg-slate-50 border border-slate-200 shadow-sm transition-opacity"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )}
                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:rotate-12 transition-transform">
                                <Scale className="w-24 h-24" />
                            </div>
                            <h3 className="text-lg font-black mb-6 flex items-center gap-3">
                                <div className="p-2 bg-white/10 rounded-xl">
                                    <ScrollText className="w-5 h-5 text-blue-400" />
                                </div>
                                Insights Legales
                            </h3>

                            <div className="space-y-6">
                                {currentRun?.metadata?.insights && currentRun.metadata.insights.length > 0 ? (
                                    currentRun.metadata.insights.map((insight: any, i: number) => (
                                        <div key={i} className="p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors animate-in fade-in duration-500">
                                            <div className={`text-[10px] font-black uppercase mb-2 ${insight.type === 'risk' ? 'text-amber-500' :
                                                    insight.type === 'precedent' ? 'text-blue-400' :
                                                        'text-emerald-400'
                                                }`}>
                                                {insight.label}
                                            </div>
                                            <p className="text-xs text-slate-300 leading-relaxed font-medium">
                                                {insight.content}
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-6 text-center">
                                        <p className="text-xs text-slate-500 font-medium">
                                            {currentRun?.status === 'streaming' || currentRun?.status === 'loading'
                                                ? 'Analizando respuesta...'
                                                : 'Los insights aparecerán aquí después de tu primera consulta.'}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <Button
                                className="w-full mt-8 rounded-2xl bg-blue-600 hover:bg-blue-700 border-none font-bold py-6 group"
                                onClick={() => activeAgent && handleAskAgent(activeAgent.id, '¿Puedes profundizar en los riesgos y excepciones de este análisis?')}
                            >
                                Profundizar Análisis
                                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
                            <h3 className="text-sm font-black text-slate-900 mb-6 flex items-center gap-3">
                                <Gavel className="w-4 h-4 text-blue-600" />
                                Normativa Citada
                            </h3>
                            <div className="space-y-4">
                                {currentRun?.metadata?.citations && currentRun.metadata.citations.length > 0 ? (
                                    currentRun.metadata.citations.map((citation: any, i: number) => (
                                        <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all cursor-pointer group animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: `${i * 80}ms` }}>
                                            <span className="text-xs font-bold text-slate-600">{citation.norm}</span>
                                            <div className={`p-1 px-2 rounded-lg text-[10px] font-black group-hover:bg-blue-600 group-hover:text-white transition-colors ${citation.type === 'Jurisprudencia' ? 'bg-purple-50 text-purple-600' :
                                                    citation.type === 'Ley' ? 'bg-blue-50 text-blue-600' :
                                                        citation.type === 'Decreto' ? 'bg-emerald-50 text-emerald-600' :
                                                            citation.type === 'Código' ? 'bg-amber-50 text-amber-600' :
                                                                'bg-slate-50 text-slate-600'
                                                }`}>{citation.type}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-4 text-center">
                                        <p className="text-xs text-slate-400 font-medium">
                                            {currentRun?.status === 'streaming' || currentRun?.status === 'loading'
                                                ? 'Extrayendo normativa...'
                                                : 'La normativa citada aparecerá aquí después del análisis.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="relative min-h-screen bg-slate-50/30 font-inter overflow-x-hidden">
            <PremiumBackground />

            <main className="relative z-10 pt-20">
                {viewMode === "ROUTER" && renderRouter()}
                {viewMode === "DIRECTORY" && renderDirectory()}
                {viewMode === "AGENT" && renderAgentWorkspace()}
            </main>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
                
                body {
                    font-family: 'Inter', sans-serif;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }

                .prose strong { color: #0f172a; font-weight: 800; }
                .prose h1, .prose h2, .prose h3 { color: #1e293b; font-weight: 900; }
                .prose p { margin-bottom: 1.25rem; }

                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-in-from-bottom {
                    from { transform: translateY(20px); }
                    to { transform: translateY(0); }
                }
                .animate-in {
                    animation-fill-mode: both;
                    animation-duration: 500ms;
                }
                .fade-in { animation-name: fade-in; }
                .slide-in-from-bottom-5 { animation-name: slide-in-from-bottom; }
            `}</style>
        </div>
    );
}
